import { generateEventId } from "./dedup";
import type { Attribution } from "./types";

// Browser-side tracking helpers: UTM/click-id capture + persistence, and the
// dataLayer push for funnel events. Imported only by client components. All
// functions guard on `typeof window` so they're inert during SSR.

const ATTR_KEY = "__aluume_attr";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const m = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : undefined;
}

function readStored(): Attribution {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(ATTR_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
}

function persist(attr: Attribution) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
  } catch {
    // sessionStorage unavailable (private mode) — non-fatal; capture is best-effort.
  }
}

/**
 * Merge this page's UTM/click-ids + ad cookies into the stored attribution and
 * persist it (sessionStorage — per-tab, survives the funnel and the payment
 * gateway round-trip). First-touch for UTM/click-ids (the original ad that brought
 * the visitor wins); fbp / _ga / fbc are refreshed from the pixels' own cookies
 * each call (they're set asynchronously after page load). A purchase_event_id is
 * minted once so the browser Purchase and the server CAPI/MP fire share a dedup key.
 * Idempotent — safe to call on every page mount and again at hold time.
 */
export function captureAttribution(): Attribution {
  const attr = readStored();
  if (typeof window === "undefined") return attr;

  const qp = new URLSearchParams(window.location.search);
  const firstTouch = (k: keyof Attribution, v: string | null) => {
    if (v && !attr[k]) (attr as Record<string, string>)[k] = v;
  };
  firstTouch("utm_source", qp.get("utm_source"));
  firstTouch("utm_medium", qp.get("utm_medium"));
  firstTouch("utm_campaign", qp.get("utm_campaign"));
  firstTouch("utm_term", qp.get("utm_term"));
  firstTouch("utm_content", qp.get("utm_content"));
  firstTouch("gclid", qp.get("gclid"));
  firstTouch("wbraid", qp.get("wbraid"));
  firstTouch("gbraid", qp.get("gbraid"));

  const fbclid = qp.get("fbclid");
  firstTouch("fbclid", fbclid);
  if (fbclid && !attr.fbc) attr.fbc = `fb.1.${Date.now()}.${fbclid}`;
  else if (!attr.fbc) {
    const c = readCookie("_fbc");
    if (c) attr.fbc = c;
  }

  const fbp = readCookie("_fbp");
  if (fbp) attr.fbp = fbp;
  const ga = readCookie("_ga"); // GA1.1.<a>.<b> → client_id = <a>.<b>
  if (ga) {
    const parts = ga.split(".");
    if (parts.length >= 4) attr.ga_client_id = `${parts[2]}.${parts[3]}`;
  }

  if (!attr.landing_page) attr.landing_page = window.location.pathname + window.location.search;
  if (!attr.referrer && document.referrer) attr.referrer = document.referrer;
  if (!attr.purchase_event_id) attr.purchase_event_id = generateEventId();

  persist(attr);
  return attr;
}

/** Read the persisted attribution without re-capturing (used on the confirmation page). */
export function readAttribution(): Attribution {
  return readStored();
}

/**
 * Push a funnel event to the GTM dataLayer. GTM tags (configured in the container)
 * forward each to GA4 + the Meta Pixel; passing a shared event_id lets Meta/Google
 * dedupe the browser event against the server-side CAPI/MP fire.
 */
export function trackClientEvent(event: string, params: Record<string, unknown> = {}, eventId?: string) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // GA4 ecommerce hygiene: null the previous ecommerce object first so items/value
  // from an earlier event don't bleed into this one (Google's recommended pattern).
  if ("ecommerce" in params) window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ...(eventId ? { event_id: eventId } : {}), ...params });
}
